import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import Logo from "../components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Public read-only view of a homeowner's report, resolved by share token.
 * No auth, no PII beyond first name. Renders persona + style assessment +
 * moodboard pin thumbnails. Expired or revoked tokens return a friendly wall.
 */
export default function SharedReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/shared/report/${token}`)
      .then((res) => setData(res.data))
      .catch((err) => setError(err.response?.data?.detail || "This link isn't available."))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--folk-bg)" }}>
      <header style={{ borderBottom: "1.5px solid var(--folk-border)", background: "var(--folk-card)", padding: "20px 32px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <Logo variant="split" size={20} />
          <span style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontFamily: "Host Grotesk, sans-serif", fontWeight: 700 }}>
            shared report · read-only
          </span>
        </div>
      </header>

      <main style={{ maxWidth: 900, margin: "0 auto", padding: "40px 24px 80px" }}>
        {loading ? (
          <div style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 18, padding: 32, color: "var(--folk-muted)", textAlign: "center" }}>
            Loading the report…
          </div>
        ) : error ? (
          <ExpiredCard message={error} />
        ) : (
          <SharedContent data={data} />
        )}
      </main>
    </div>
  );
}

function ExpiredCard({ message }) {
  return (
    <div data-testid="shared-report-error" style={{ background: "#FFFBC9", border: "1.5px solid rgba(104, 40, 14, 0.25)", borderRadius: 20, padding: 32, textAlign: "center", color: "#68280E" }}>
      <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.028em", fontSize: 28, textTransform: "lowercase", marginBottom: 12 }}>
        this link isn't available.
      </div>
      <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 15, lineHeight: 1.55, marginBottom: 20 }}>
        {message}
      </div>
      <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 13.5, color: "var(--folk-muted)" }}>
        Ask the homeowner to send a fresh link, or reach out to <a href="mailto:team@folkobjects.com" style={{ color: "#68280E", fontWeight: 600 }}>team@folkobjects.com</a>.
      </div>
    </div>
  );
}

function SharedContent({ data }) {
  const first = (data.contact_name || "your friend").split(" ")[0].toLowerCase();
  const persona = data.persona_summary;
  const assessment = data.style_assessment;
  const pins = (data.moodboard_pins || []).filter((p) => p.image_url).slice(0, 12);
  const expiresLabel = formatExpires(data.expires_at);

  return (
    <div data-testid="shared-report-content" style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      <div>
        <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-hint)", fontWeight: 700, fontFamily: "Host Grotesk, sans-serif" }}>Folk Objects · homeowner report</div>
        <h1 className="folk-h1" style={{ marginTop: 6, marginBottom: 8 }}>
          {first}'s design report.
        </h1>
        <p style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 15, color: "var(--folk-muted)", lineHeight: 1.55, margin: 0 }}>
          Everything we heard, translated into palette, textures and a moodboard direction. This is a read-only view — nothing here can be edited.
        </p>
        {expiresLabel && (
          <div data-testid="shared-report-expiry" style={{ marginTop: 10, fontSize: 12.5, color: "#0F3A54", fontFamily: "Host Grotesk, sans-serif", background: "#BADDF0", border: "1.5px solid rgba(15, 58, 84, 0.25)", padding: "6px 12px", borderRadius: 999, display: "inline-block" }}>
            Link expires {expiresLabel}
          </div>
        )}
      </div>

      {persona && (
        <div data-testid="shared-persona" style={{ background: "#FFFBC9", border: "1.5px solid rgba(104, 40, 14, 0.18)", borderRadius: 20, padding: 28 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "#68280E", fontWeight: 700, marginBottom: 12 }}>Who you are, to us</div>
          <div style={{ fontFamily: "'Playfair Display', Georgia, serif", letterSpacing: "-0.015em", fontSize: 17.5, lineHeight: 1.7, color: "#1B1A1A", whiteSpace: "pre-wrap" }}>
            {persona}
          </div>
        </div>
      )}

      {assessment && (
        <>
          {assessment.palette?.length > 0 && (
            <div data-testid="shared-palette" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 20, padding: 24 }}>
              <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700, marginBottom: 12 }}>Palette</div>
              <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(assessment.palette.length, 6)}, minmax(0, 1fr))`, gap: 10 }}>
                {assessment.palette.map((c, i) => (
                  <div key={i}>
                    <div title={c.hex} style={{ aspectRatio: "1 / 1.05", borderRadius: 14, border: "1.5px solid var(--folk-border)", background: c.hex }} />
                    <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 12.5, color: "#2A2828", marginTop: 8, textTransform: "lowercase" }}>{c.name}</div>
                    <div style={{ fontFamily: "Host Grotesk, sans-serif", fontSize: 11.5, color: "var(--folk-muted)", letterSpacing: "0.04em" }}>{c.hex}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(assessment.textures?.length > 0 || assessment.keywords?.length > 0) && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 16 }}>
              {assessment.textures?.length > 0 && (
                <ChipCard title="Materials & textures" items={assessment.textures} testId="shared-textures" />
              )}
              {assessment.keywords?.length > 0 && (
                <ChipCard title="Keywords" items={assessment.keywords} color="#0F3A54" testId="shared-keywords" />
              )}
            </div>
          )}
        </>
      )}

      {pins.length > 0 && (
        <div data-testid="shared-moodboard" style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 20, padding: 24 }}>
          <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color: "var(--folk-accent)", fontWeight: 700, marginBottom: 12 }}>Moodboard</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 10 }}>
            {pins.map((p) => (
              <a key={p.id} href={p.image_url} target="_blank" rel="noreferrer" style={{ display: "block", aspectRatio: "1/1", borderRadius: 12, overflow: "hidden", background: "#F3ECE9", border: "1.5px solid var(--folk-border)" }}>
                <img src={p.image_url?.startsWith("http") ? p.image_url : `${process.env.REACT_APP_BACKEND_URL}${p.image_url}`} alt="" loading="lazy" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
              </a>
            ))}
          </div>
        </div>
      )}

      <div style={{ textAlign: "center", padding: "18px 0", fontFamily: "Host Grotesk, sans-serif", fontSize: 13, color: "var(--folk-muted)" }}>
        Want your own? Start at{" "}
        <Link to="/" style={{ color: "#68280E", fontWeight: 600, textDecoration: "underline" }}>folkobjects.com</Link>.
      </div>
    </div>
  );
}

function ChipCard({ title, items, color = "var(--folk-accent)", testId }) {
  return (
    <div data-testid={testId} style={{ background: "var(--folk-card)", border: "1.5px solid var(--folk-border)", borderRadius: 20, padding: 24 }}>
      <div style={{ fontSize: 12, letterSpacing: "0.14em", textTransform: "uppercase", color, fontWeight: 700, marginBottom: 12 }}>{title}</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {items.map((t, i) => (
          <span key={i} style={{ display: "inline-flex", padding: "6px 12px", borderRadius: 999, border: `1.5px solid ${color}`, color, fontFamily: "Host Grotesk, sans-serif", fontSize: 13 }}>
            {String(t).toLowerCase()}
          </span>
        ))}
      </div>
    </div>
  );
}

function formatExpires(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    const now = new Date();
    const days = Math.ceil((d.getTime() - now.getTime()) / (24 * 60 * 60 * 1000));
    if (days <= 0) return "today";
    if (days === 1) return "tomorrow";
    return `in ${days} days · ${d.toLocaleDateString()}`;
  } catch { return null; }
}
